import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const TermsOfService = () => {
  return (
    <>
      <SEO 
        title="Terms of Service - BlogCraft"
        description="BlogCraft's Terms of Service - Read the terms and conditions for using our website and services."
        keywords="terms of service, terms and conditions, legal terms, blogcraft terms"
      />

      <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Terms of Service</h1>
          <p className="text-blue-100">Last updated: January 3, 2026</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="prose prose-lg max-w-none">
              
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Agreement to Terms</h2>
                <p className="text-gray-700 mb-4">
                  By accessing and using BlogCraft (blogcraft.shop), you accept and agree to be bound by the terms 
                  and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                </p>
                <p className="text-gray-700">
                  These Terms of Service govern your use of our website, services, and content. By using BlogCraft, 
                  you represent that you are at least 13 years of age and have the legal capacity to enter into these terms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Description of Service</h2>
                <p className="text-gray-700 mb-4">
                  BlogCraft is an online platform that provides:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Blog articles and content on various topics</li>
                  <li>Newsletter subscription services</li>
                  <li>Comment and interaction features</li>
                  <li>Contact and inquiry forms</li>
                  <li>Educational and informational content</li>
                </ul>
                <p className="text-gray-700">
                  We reserve the right to modify, suspend, or discontinue any part of our service at any time without notice.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">User Responsibilities</h2>
                <p className="text-gray-700 mb-4">As a user of BlogCraft, you agree to:</p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Provide accurate and complete information when required</li>
                  <li>Use the service for lawful purposes only</li>
                  <li>Not attempt to gain unauthorized access to our systems</li>
                  <li>Not interfere with or disrupt the service</li>
                  <li>Not upload malicious code or viruses</li>
                  <li>Not spam or send unsolicited communications</li>
                  <li>Respect the rights of other users</li>
                </ul>
                <p className="text-gray-700">
                  You are solely responsible for your use of the service and any consequences resulting from such use.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Content and Intellectual Property</h2>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Our Content</h3>
                <p className="text-gray-700 mb-4">
                  All content on BlogCraft, including articles, images, graphics, and design elements, is owned by 
                  BlogCraft or our content partners and is protected by copyright, trademark, and other intellectual property laws.
                </p>
                <p className="text-gray-700 mb-4">
                  You may not reproduce, distribute, modify, create derivative works, or publicly display our content 
                  without our prior written consent.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">User-Generated Content</h3>
                <p className="text-gray-700 mb-4">
                  When you submit content to BlogCraft (such as comments or feedback), you grant us a non-exclusive, 
                  worldwide, royalty-free license to use, reproduce, and display such content in connection with our service.
                </p>
                <p className="text-gray-700">
                  You retain ownership of your content but represent that you have the right to grant such license.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Privacy</h2>
                <p className="text-gray-700 mb-4">
                  Your privacy is important to us. Please review our <Link to="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</Link>, 
                  which also governs your use of our service, to understand our practices.
                </p>
                <p className="text-gray-700">
                  By using BlogCraft, you consent to the collection and use of information in accordance with our Privacy Policy.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Disclaimer of Warranties</h2>
                <p className="text-gray-700 mb-4">
                  BlogCraft is provided on an "as is" and "as available" basis. We make no representations or warranties 
                  of any kind, express or implied, as to the operation of our service or the information, content, materials, 
                  or products included on this website.
                </p>
                <p className="text-gray-700">
                  We do not warrant that the service will be uninterrupted, timely, secure, or error-free. We disclaim all 
                  warranties, express or implied, including but not limited to implied warranties of merchantability and fitness 
                  for a particular purpose.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Limitation of Liability</h2>
                <p className="text-gray-700 mb-4">
                  In no event shall BlogCraft, our directors, employees, partners, agents, suppliers, or affiliates be liable 
                  for any indirect, incidental, special, consequential, or punitive damages, including without limitation, 
                  loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the service.
                </p>
                <p className="text-gray-700">
                  Our total liability to you for any cause of action whatsoever, and regardless of the form of the action, 
                  will at all times be limited to the amount paid, if any, by you to us for the service during the term of 
                  your use of the service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Indemnification</h2>
                <p className="text-gray-700">
                  You agree to defend, indemnify, and hold harmless BlogCraft and our licensee and licensors, and their 
                  employees, contractors, agents, officers and directors, from and against any and all claims, damages, 
                  obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees).
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Termination</h2>
                <p className="text-gray-700 mb-4">
                  We may terminate or suspend your access immediately, without prior notice or liability, for any reason 
                  whatsoever, including without limitation if you breach the Terms.
                </p>
                <p className="text-gray-700">
                  Upon termination, your right to use the service will cease immediately. All provisions of the Terms 
                  which by their nature should survive termination shall survive, including ownership provisions, 
                  warranty disclaimers, and limitations of liability.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Governing Law</h2>
                <p className="text-gray-700">
                  These Terms shall be interpreted and governed by the laws of the jurisdiction in which BlogCraft operates, 
                  without regard to its conflict of law provisions.
                </p>
                <p className="text-gray-700">
                  Any legal action or proceeding arising under these Terms will be brought exclusively in the courts 
                  of the appropriate jurisdiction.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to Terms</h2>
                <p className="text-gray-700">
                  We reserve the right to modify these Terms at any time. If we make material changes, we will notify 
                  you by email or by posting a notice on our website prior to the effective date of the changes.
                </p>
                <p className="text-gray-700">
                  Your continued use of the service after any such changes constitutes your acceptance of the new Terms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
                <p className="text-gray-700 mb-4">
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700"><strong>Website:</strong> <Link to="/contact" className="text-blue-600 hover:underline">Contact Form</Link></p>
                  <p className="text-gray-700"><strong>Response Time:</strong> We will respond to your inquiry within 7 business days.</p>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsOfService;
